import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { jsonFetch } from 'ofe-common/utilities';
import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';
import { ServicesFilter, ServicesObject, ServicesImportantMessage } from '@customer/types/Services';

export const useServicesStore = defineStore('services', () => {
	const state = {
		displaySaveChanges: ref(false),
		displayAPToggler: ref(false),
		isNewCategory: ref(false),
		currentPage: ref(''),
		selectedCategoryGroupId: ref(0),
		groupsToRemove: ref<{
			isSelected: boolean;
			categoryId: number;
			categoryName: string;
		}[]>([]),
		selectedFilterIndex: ref(0),
		isEditMatching: ref(false),
		isEditLocations: ref(false),
		isFreshCategory: ref(false),
		suspendFilterStatus: ref(''),
		filterToRemove: ref({
			id: 0,
			type: '',
			categoryId: 0,
		}),
		isFilterDirty: ref(false),
		isInsideFilterDetails: ref(false),
		pricingCategoryId: ref(0),
		customerId: ref(useAppStore().isAdminModule ? Number(useRoute()?.params?.id) : useUserStore().customerId),
		testOFE35297: ref<'A' | 'B' | 'C' | undefined>(undefined),
		isRecommendationFeedbackModalVisible: ref(false),
		notifications: ref({
			recommendation: {
				type: '',
				title: '',
				message: '',
				button: '',
				dismissButton: '',
				moreInfoLink: '',
				isVisible: false,
			},
		}),
		invalidStats: ref(false),
		isLoading: ref(false),
		isFreeRecommendationConfirmationModalVisible: ref(false),
		potentialMatches: ref(-1),
		visibilityTenPercentDiscountConfirmationModal: false,
		isAutoGenerateChatMessages: false,
		automaticFilterFromRfp: ref(false),
		inExtraMatching: ref(false),
		isExtraMatchingOnCurrentUser: ref(false),
	};

	const actions = {
		async loadInExtraMatching() {
			const { isAdminModule, hasFlag } = useAppStore() 
			try {
				const showToggleResponse = isAdminModule ? await jsonFetch<{ value: boolean }>(`/myoferteo/in-extra-matching.do?idCustomer=${state.customerId.value}`, { method: 'GET' }) : undefined;
				const showToggleThroughFlag = hasFlag('inExtraMatching');
				state.isExtraMatchingOnCurrentUser.value = (isAdminModule && showToggleResponse?.value) || (!isAdminModule && showToggleThroughFlag);

				const result = await jsonFetch<{ value: boolean }>(`/myoferteo/extra-matching-toogle-value.do${isAdminModule ? '?idCustomer=' + state.customerId.value : ''}`, {
					method: 'GET',
				});
				state.inExtraMatching.value = result.value;
			} catch (error) {
				console.error('Error loading inExtraMatching:', error);
			}
		},
		async setInExtraMatching() {
			const { isAdminModule } = useAppStore() 
			const payload = state?.inExtraMatching?.value;
			const errorMessages = {'TypeError: Failed to fetch': 'Nie można połączyć się z serwerem. Sprawdź połączenie internetowe.'};
			try {
				const response = await jsonFetch<{ success: boolean }>(`/myoferteo/set-extra-matching-toogle-value/${Boolean(payload)}.do`, {
					method: 'POST',
					...(isAdminModule && {
						body: {
						idCustomer: state.customerId.value,
					}})
				});
				if (!response.success) {
					throw new Error(response.responseMessage);
					return [null, 'Nie można ustawić wartości'];
				}
				return [response.success, null];
			} catch (error) {
				console.error('Error setting inExtraMatching:', error);
				return [null, error || errorMessages[error]];
			}
			return [null, 'wystąpił nieznany błąd'];
		},
		setTestOFE35297() {
			state.testOFE35297.value = 'A';
		},
		/**
		 * Zwraca tablicę z aktualnym filtrem oraz tym znajdującym się w drzewie.
		 * Używamy tego sposobu, kiedy chcemy zmienić jakąś informację, a nie chcemy
		 * pobierać całej listy na nowo
		 */
		selectedFilters({ selectedFilter, servicesObject }: { selectedFilter: ServicesFilter; servicesObject: ServicesObject }): ServicesFilter[] {
			// @TODO - przenieść selectedFilter, servicesObject do store
			const { categoryId } = selectedFilter.category;
			const foundGroup = servicesObject.tree.find((group) => group.categoryId === categoryId);
			const foundFilter = foundGroup?.filters.find((filter) => filter.id === selectedFilter.id);

			return foundFilter ? [selectedFilter, foundFilter] : [];
		},
		setNotifications(apImportantMessage: ServicesImportantMessage) {
			state.notifications.value.recommendation = {
				type: apImportantMessage.type || 'info',
				title: apImportantMessage.title,
				message: apImportantMessage.message.replace(/<br>/g, ''),
				button: apImportantMessage.buttonLabel || 'Dowiedz się więcej',
				dismissButton: apImportantMessage.dismissButtonLabel || 'Przypomnij później',
				moreInfoLink: apImportantMessage.moreInfoLink,
				isVisible: true,
			};
		},
		labelClasses(filter?: ServicesFilter) {
			if (!filter || !filter.isActive) {
				return 'bg-[#F2F2F2] text-[#666666]';
			}

			if (actions.isFilterInactive(filter)) {
				return 'bg-[#FBEAE8] text-[#DA291C]';
			}

			if (filter.tsSuspendedEnd && actions.isSuspended(filter)) {
				return 'bg-[#E7EFFB] text-[#1462D3]';
			}

			return 'bg-[#E7F3EF] text-[#0E875F]';
		},

		/**
		 * Czy filtr jest nieaktywny? (filter.isActive === false a isFilterInactive to nie to samo,
		 * pierwsze oznacza że został wyłączony ręcznie, drugie że został wyłączony automatycznie przez niespełniające warunki serwisu)
		 */
		isFilterInactive(filter: ServicesFilter) {
			return filter.type === 'automatic' && (
				filter.notEnoughPoints
				|| filter.finalMarkBelowApThreshold
				|| filter.weeklyLimitReached
				|| !filter.apValidated
			);
		},

		/**
		 * Sprawdza, czy podany filtr Polecania lub Szybkiego Kontaktu jest zawieszony.
		 * @param {ServicesFilter} filter - Filtr do sprawdzenia
		 */
		isSuspended(filter: ServicesFilter) {
			return filter.isActive
			  && filter.tsSuspendedEnd
			  && filter.tsSuspendedEnd > Date.now()
			  && (filter.type === 'automatic' || filter.type === 'quick');
		},
	};

	const getters = {};

	return { ...state, ...actions, ...getters };
});
